import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search } from 'lucide-react';
import { fetchPriceBook, savePriceBookItem, type PriceBookItem } from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import { Card, CardHeader } from '../components/ui/Card';
import { fieldControlClass, fieldControlSmClass } from '../components/ui/forms';

// Price Book viewer, plus editing for Owner/Admin/Manager accounts with
// canEditPrices (server-reported via `canEdit` on the workspace response —
// appSavePriceBookItem itself enforces this, this UI just hides the
// controls when the server wouldn't accept them). Every save sends the
// full existing item back (spread + overrides), not just the changed
// field — appSavePriceBookItem overwrites the whole Price Book row from
// its payload, so submitting a partial object would silently blank out
// every field this form doesn't know about.
export function PriceBookPage() {
  const [search, setSearch] = useState('');
  const query = useQuery({ queryKey: ['priceBook'], queryFn: fetchPriceBook });
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [priceDraft, setPriceDraft] = useState('');
  const [activeDraft, setActiveDraft] = useState(true);

  const saveMutation = useMutation({
    mutationFn: (item: PriceBookItem) => savePriceBookItem({ ...item, currentPrice: Number(priceDraft || 0), active: activeDraft }),
    onSuccess: (result) => {
      if (result.ok) {
        setEditingId(null);
        void queryClient.invalidateQueries({ queryKey: ['priceBook'] });
      }
    },
  });

  const grouped = useMemo(() => {
    const items = query.data?.items ?? [];
    const q = search.trim().toLowerCase();
    const filtered = q ? items.filter((i) => `${i.name} ${i.category} ${i.description ?? ''}`.toLowerCase().includes(q)) : items;
    const byCategory = new Map<string, PriceBookItem[]>();
    filtered
      .filter((i) => i.active || editingId === i.serviceId)
      .forEach((item) => {
        const list = byCategory.get(item.category) ?? [];
        list.push(item);
        byCategory.set(item.category, list);
      });
    return byCategory;
  }, [query.data, search, editingId]);

  if (query.isLoading) return <LoadingState label="Loading Price Book…" />;
  if (query.isError) {
    return <ErrorState message={query.error instanceof Error ? query.error.message : 'Could not load the Price Book.'} onRetry={() => query.refetch()} />;
  }
  if (!query.data?.ok) {
    return <ErrorState message={query.data?.message || 'Could not load the Price Book.'} onRetry={() => query.refetch()} />;
  }

  const canEdit = !!query.data.canEdit;

  return (
    <div className="space-y-5">
      <div>
        <Link to="/estimates" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold-300 hover:text-gold-200">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Estimates
        </Link>
        <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-white">Price Book</h1>
      </div>
      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden="true" />
        <input className={`${fieldControlClass} pl-9`} placeholder="Search services…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {grouped.size === 0 && <EmptyState>No services found.</EmptyState>}

      {Array.from(grouped.entries()).map(([category, items]) => (
        <Card key={category}>
          <CardHeader title={category} />
          <ul className="space-y-2">
            {items.map((item) => (
              <li key={item.serviceId} className="rounded-xl border border-ink-border bg-ink p-3">
                {editingId === item.serviceId ? (
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-white">{item.name}</p>
                    </div>
                    <div className="w-28 space-y-1">
                      <label className="text-[10px] font-semibold uppercase text-slate-500">Price</label>
                      <input type="number" min="0" step="0.01" className={fieldControlSmClass} value={priceDraft} onChange={(e) => setPriceDraft(e.target.value)} />
                    </div>
                    <label className="flex items-center gap-1.5 text-xs text-slate-300">
                      <input type="checkbox" checked={activeDraft} onChange={(e) => setActiveDraft(e.target.checked)} />
                      Active
                    </label>
                    <Button busy={saveMutation.isPending} busyLabel="Saving…" onClick={() => saveMutation.mutate(item)}>
                      Save
                    </Button>
                    <Button variant="secondary" onClick={() => setEditingId(null)}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-white">{item.name}</p>
                      {item.description && <p className="truncate text-sm text-slate-400">{item.description}</p>}
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="text-sm font-semibold text-gold-200">
                        {item.currentPriceText} / {item.unit}
                      </span>
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(item.serviceId);
                            setPriceDraft(String(item.currentPrice ?? 0));
                            setActiveDraft(item.active);
                          }}
                          className="text-xs font-semibold text-gold-300 hover:text-gold-200"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Card>
      ))}
      {saveMutation.data && !saveMutation.data.ok && <ErrorState message={saveMutation.data.message || 'Could not save this Price Book item.'} />}
    </div>
  );
}
